import React, { useState, useEffect } from 'react';
import API_BASE_URL from './config';

function AuditLogViewer({ currentUser, theme, toggleTheme }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        limit: 100,
        entity_type: '',
        user: '',
        action: ''
    });
    const [expandedLog, setExpandedLog] = useState(null);

    useEffect(() => {
        fetchLogs();
    }, [filters]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('limit', filters.limit);
            if (filters.entity_type) params.append('entity_type', filters.entity_type);
            if (filters.user) params.append('filter_user', filters.user);

            const response = await fetch(`${API_BASE_URL}/audit_logs?${params}`, {
                credentials: 'include'
            });
            const data = await response.json();
            
            if (data.error) {
                console.error('Error fetching logs:', data.error);
            } else {
                let filteredLogs = data.logs || [];
                
                // Client-side filter by action if specified
                if (filters.action) {
                    filteredLogs = filteredLogs.filter(log => 
                        log.action.toLowerCase().includes(filters.action.toLowerCase())
                    );
                }
                
                setLogs(filteredLogs);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching logs:', err);
            setLoading(false);
        }
    };

    const getActionIcon = (action) => {
        const icons = {
            'city_added': '➕',
            'city_deleted': '🗑️',
            'city_config_changed': '⚙️',
            'scraper_run': '🔍',
            'job_status_changed': '✏️',
            'job_work_type_changed': '🏷️',
            'job_edited': '📝',
            'contact_created': '👤',
            'contact_updated': '✏️',
            'contact_deleted': '🗑️',
            'contact_assigned_to_job': '🔗',
            'user_created': '👥',
            'user_updated': '✏️',
            'user_deleted': '🗑️',
            'password_changed': '🔐',
            'database_sync': '🔄'
        };
        return icons[action] || '📋';
    };

    const getActionColor = (action) => {
        if (action.includes('created') || action.includes('added')) return 'success';
        if (action.includes('deleted')) return 'danger';
        if (action.includes('updated') || action.includes('changed') || action.includes('edited')) return 'warning';
        if (action.includes('scraper')) return 'info';
        return 'secondary';
    };

    const getEntityTypeIcon = (entityType) => {
        const icons = {
            'city': '🏙️',
            'job': '📋',
            'contact': '👤',
            'user': '👥',
            'contact_job_assignment': '🔗',
            'database': '💾',
            'city_link': '🔗'
        };
        return icons[entityType] || '📄';
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        
        return date.toLocaleString();
    };

    const formatValue = (value) => {
        if (!value) return null;
        try {
            const parsed = JSON.parse(value);
            return (
                <pre className="mb-0" style={{ fontSize: '0.85rem' }}>
                    {JSON.stringify(parsed, null, 2)}
                </pre>
            );
        } catch {
            return value;
        }
    };

    const getActionDescription = (log) => {
        const action = log.action;
        const entity = log.entity_id;
        const user = log.user;

        if (action === 'city_added') return `${user} added city "${entity}"`;
        if (action === 'city_deleted') return `${user} deleted city "${entity}"`;
        if (action === 'city_config_changed') return `${user} updated configuration for "${entity}"`;
        if (action === 'scraper_run') return `Scraper ran for "${entity}"`;
        if (action === 'job_status_changed') return `${user} changed job status for ${entity}`;
        if (action === 'job_work_type_changed') return `${user} changed work type for ${entity}`;
        if (action === 'job_edited') return `${user} edited job ${entity}`;
        if (action === 'contact_created') return `${user} created contact #${entity}`;
        if (action === 'contact_updated') return `${user} updated contact #${entity}`;
        if (action === 'contact_deleted') return `${user} deleted contact #${entity}`;
        if (action === 'contact_assigned_to_job') return `${user} assigned contact to job`;
        if (action === 'user_created') return `${user} created user "${entity}"`;
        if (action === 'user_updated') return `${user} updated user "${entity}"`;
        if (action === 'user_deleted') return `${user} deleted user "${entity}"`;
        if (action === 'password_changed') return `${user} changed their password`;
        if (action === 'database_sync') return `${user} synced database`;
        
        return `${user} → ${action} → ${entity}`;
    };

    if (loading) {
        return <div className="text-center mt-5"><div className="spinner-border" /></div>;
    }

    const handleExportToExcel = async () => {
        try {
            // Build URL with current filter parameters
            let apiUrl = `${API_BASE_URL}/export/audit_logs?`;
            const params = [];
            
            params.push(`limit=${filters.limit}`);
            if (filters.entity_type) {
                params.push(`entity_type=${encodeURIComponent(filters.entity_type)}`);
            }
            if (filters.user) {
                params.push(`filter_user=${encodeURIComponent(filters.user)}`);
            }
            
            apiUrl += params.join('&');
            
            const response = await fetch(apiUrl, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Export failed');
            }
            
            // Get the blob from response
            const blob = await response.blob();
            
            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            
            // Get filename from Content-Disposition header if available
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `audit_logs_${new Date().toISOString().split('T')[0]}.xlsx`;
            if (contentDisposition) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }
            
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            
            // Cleanup
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
            
            alert('✅ Audit logs exported to Excel successfully!');
        } catch (error) {
            console.error('Error exporting audit logs:', error);
            alert('❌ Failed to export audit logs. Please try again.');
        }
    };

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>📊 Audit Log Viewer</h2>
                <div>
                    <button 
                        onClick={handleExportToExcel}
                        className="btn btn-primary me-2"
                        title="Export audit logs to Excel"
                    >
                        📊 Export to Excel
                    </button>
                    <button 
                        onClick={toggleTheme} 
                        className="theme-toggle"
                        title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                    >
                        {theme === 'light' ? '🌙' : '☀️'}
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">Filters</h5>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">Limit</label>
                            <select
                                className="form-select"
                                value={filters.limit}
                                onChange={(e) => setFilters({...filters, limit: e.target.value})}
                            >
                                <option value="50">Last 50</option>
                                <option value="100">Last 100</option>
                                <option value="200">Last 200</option>
                                <option value="500">Last 500</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Entity Type</label>
                            <select
                                className="form-select"
                                value={filters.entity_type}
                                onChange={(e) => setFilters({...filters, entity_type: e.target.value})}
                            >
                                <option value="">All Types</option>
                                <option value="city">🏙️ Cities</option>
                                <option value="job">📋 Jobs</option>
                                <option value="contact">👤 Contacts</option>
                                <option value="user">👥 Users</option>
                                <option value="database">💾 Database</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">User</label>
                            <select
                                className="form-select"
                                value={filters.user}
                                onChange={(e) => setFilters({...filters, user: e.target.value})}
                            >
                                <option value="">All Users</option>
                                <option value={currentUser?.username}>My Actions</option>
                                <option value="system">System</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Action</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Filter by action..."
                                value={filters.action}
                                onChange={(e) => setFilters({...filters, action: e.target.value})}
                            />
                        </div>
                    </div>
                    <div className="mt-3">
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setFilters({limit: 100, entity_type: '', user: '', action: ''})}
                        >
                            Clear Filters
                        </button>
                        <button
                            className="btn btn-primary btn-sm ms-2"
                            onClick={fetchLogs}
                        >
                            🔄 Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <div className="card">
                <div className="card-body">
                    <h5 className="card-title">Activity Timeline ({logs.length} events)</h5>
                    
                    {logs.length === 0 ? (
                        <p className="text-muted">No audit logs found matching your filters.</p>
                    ) : (
                        <div className="timeline">
                            {logs.map((log) => (
                                <div key={log.id} className="timeline-item mb-3">
                                    <div className={`card border-${getActionColor(log.action)}`}>
                                        <div 
                                            className="card-body"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                                        >
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span className="fs-4 me-2">
                                                            {getActionIcon(log.action)}
                                                        </span>
                                                        <div>
                                                            <strong>{getActionDescription(log)}</strong>
                                                            <div className="text-muted small">
                                                                {getEntityTypeIcon(log.entity_type)} {log.entity_type} 
                                                                {log.source && ` • ${log.source}`}
                                                                {log.ip_address && ` • ${log.ip_address}`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    <div className="text-muted small">
                                                        {formatTimestamp(log.timestamp)}
                                                    </div>
                                                    <span className={`badge bg-${getActionColor(log.action)}`}>
                                                        {log.action.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                            </div>

                                            {expandedLog === log.id && (
                                                <div className="mt-3 pt-3 border-top">
                                                    <div className="row">
                                                        {log.old_value && (
                                                            <div className="col-md-6">
                                                                <h6 className="text-danger">Before:</h6>
                                                                {formatValue(log.old_value)}
                                                            </div>
                                                        )}
                                                        {log.new_value && (
                                                            <div className="col-md-6">
                                                                <h6 className="text-success">After:</h6>
                                                                {formatValue(log.new_value)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {log.details && (
                                                        <div className="mt-2">
                                                            <h6>Details:</h6>
                                                            {formatValue(log.details)}
                                                        </div>
                                                    )}
                                                    <div className="mt-2 text-muted small">
                                                        <strong>Full Timestamp:</strong> {new Date(log.timestamp).toLocaleString()} | 
                                                        <strong> ID:</strong> {log.id}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4">
                <h5>Color Legend:</h5>
                <div className="d-flex flex-wrap gap-3 mb-4">
                    <div>
                        <span className="badge bg-success me-2">Green</span>
                        <span className="text-muted">Created/Added</span>
                    </div>
                    <div>
                        <span className="badge bg-danger me-2">Red</span>
                        <span className="text-muted">Deleted/Removed</span>
                    </div>
                    <div>
                        <span className="badge bg-warning me-2">Yellow</span>
                        <span className="text-muted">Updated/Changed</span>
                    </div>
                    <div>
                        <span className="badge bg-info me-2">Blue</span>
                        <span className="text-muted">Scraper/System</span>
                    </div>
                    <div>
                        <span className="badge bg-secondary me-2">Gray</span>
                        <span className="text-muted">Other Actions</span>
                    </div>
                </div>
                
                <h5>About Audit Logs:</h5>
                <ul>
                    <li><strong>Debugging:</strong> Track down issues by viewing system history</li>
                    <li><strong>Security:</strong> Monitor who made what changes and when</li>
                    <li><strong>Compliance:</strong> Full audit trail for all operations</li>
                    <li><strong>Click to expand:</strong> Click any log entry to see detailed before/after states</li>
                    <li><strong>Permanent record:</strong> Logs are never deleted</li>
                </ul>
            </div>
        </div>
    );
}

export default AuditLogViewer;

